/**
 * WsManager: one Hyperliquid WebSocket. The app holds one for each socket of wsSockets.ts; the backend holds its own.
 *
 * - Lazy: the socket opens on the first subscribe (or connect()).
 * - Ref-counted subscriptions keyed by canonical JSON of the subscription object; many listeners
 *   share one HL subscription. The last listener leaving sends `unsubscribe` at once.
 * - Keepalive: `{"method":"ping"}` every pingIntervalMs (HL closes idle sockets after ~60 s and answers
 *   `{"channel":"pong"}`). If nothing at all arrives within pongTimeoutMs of a ping the socket is treated
 *   as dead and replaced.
 * - Reconnect: exponential backoff with jitter; every live subscription is re-sent on open, then the
 *   onReconnect hooks fire so data hooks can re-snapshot over REST (incremental streams such as
 *   orderUpdates / userFills lose events while disconnected). Only a socket that stayed up stableResetMs clears the
 *   backoff, so one that opens and closes at once keeps slowing down (#1809: it may be a limit at work).
 * - New-socket cap (#1809, WS_DESIGN_1809 section 3): HL allows 30 new connections a minute per IP. A ConnectBudget
 *   counts the last minute's sockets that opened and, apart, attempts that never did, so a dead link spends nothing of
 *   what recovery needs while a host refusing handshakes stays bounded. Failed attempts are paced rather than left to
 *   run out, so one always comes soon after the link returns. Past a limit an attempt waits for a slot, however it was
 *   asked for (backoff, `online` event, env switch). HlProvider shares one across all sockets of the tab; the default is no cap.
 * - idleCloseMs (#1809, W11): a socket left with no subscriptions that long closes as if it never opened, keeping its
 *   hooks. The default is to stay open.
 * - Routing: channel + subscription identity (coin for market streams, user (+dex) for user streams).
 *   HL echoes subscriptions with extra defaults (e.g. l2Book gets nSigFigs/mantissa/fast), so acks are
 *   never matched by exact JSON; data frames are matched on the identifying fields only.
 * - Late listeners (#706): HL only sends a stream's snapshot when the subscription is first made. A listener that joins
 *   an l2Book or allMids someone else holds would wait for the next frame, so these keep their last frame (REPLAYED) and
 *   give it to the newcomer asynchronously (microtask) with `meta.replay = true`. The frame is kept across a reconnect as
 *   a stale-but-useful first paint. Nothing else needs it: the user session holds the account's streams and reads its
 *   rows itself, and only TradesTape listens to `trades`.
 * - One instrument (#1809, WS_DESIGN_1809 W9): only the open market holds l2Book, bbo and trades, so leaving it
 *   unsubscribes them at once, and the new one starts from its first frame, which is the full book.
 * - suspend() / resume() (#1809, W12): a sleeping tab holds no socket (tabSleep.ts). Subscriptions, hooks and the last frames
 *   stay, nothing connects until resume(), and the reopening counts as a reconnect, so the same hooks re-sync what was missed.
 * - close() tears everything down (env switch). The instance stays reusable: a later subscribe reconnects.
 */
import { canonicalJson } from './canonical';
import type { Subscription, SubscriptionData, SubscriptionType, WsFrame } from './types';

export type WsState = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** Minimal WebSocket surface we use (browser WebSocket, Node 22 WebSocket, `ws`, or a test fake). */
export interface WebSocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

export type WebSocketFactory = (url: string) => WebSocketLike;

/**
 * New sockets a tab may start in a minute, across all its sockets. HL allows 30 per IP and tabs do not know of each
 * other, so 5 tabs, the most that fit 10 sockets per IP at 2 each, reach 25 and leave 5 for reloads. 6 would leave none.
 * This is newSocketBudget (wsSockets.ts) at the default two sockets.
 */
export const MAX_NEW_SOCKETS_PER_MIN = 5;

/**
 * Ceiling on attempts that never opened in a tab's minute. Whether HL counts these at all is unknown (WS_DESIGN section 9),
 * so ConnectBudget also paces them: past the opened limit's worth in the window, one every window / that limit. A dead
 * link at two sockets makes 10 in its first minute and 5 in each after, the rate that already fits 30 at 5 tabs.
 */
export const MAX_FAILED_SOCKETS_PER_MIN = 12;

/** A socket must stay up this long before the backoff starts again from the first step. */
export const STABLE_RESET_MS = 30_000;

/** Counted for the real-browser test of hidden tabs (window.__hip4Ws, tabSleep.ts). */
export interface WsCounters {
  /** Open sockets closed by suspend(). */
  closes: number;
  /** Sockets opened again after resume(). */
  reopens: number;
  /** Sockets dropped because nothing came within pongTimeoutMs of a ping, whatever the reason: a frozen tab counts. */
  missedPongs: number;
  /** Milliseconds from the last return to every socket in use being open. */
  lastReopenMs: number;
}

type AttemptOutcome = 'opened' | 'failed';

/** One connect attempt; until it is settled it holds a slot of both limits. */
export interface ConnectAttempt {
  at: number;
  outcome: AttemptOutcome | null;
}

/** How soon to look again when only an attempt still in flight stands in the way. */
const IN_FLIGHT_RECHECK_MS = 1_000;

/** Sliding-window counts of new sockets. Managers given the same one are capped together. */
export class ConnectBudget {
  private attempts: ConnectAttempt[] = [];

  constructor(
    private readonly max = MAX_NEW_SOCKETS_PER_MIN,
    private readonly windowMs = 60_000,
    private readonly failedMax = MAX_FAILED_SOCKETS_PER_MIN,
  ) {}

  /** Milliseconds until a new socket may start; 0 means now. */
  waitMs(now = Date.now()): number {
    this.attempts = this.attempts.filter((a) => now - a.at < this.windowMs);
    const wait = Math.max(this.waitFor('opened', this.max, now), this.waitForFailed(now));
    return wait > 0 && this.attempts.some((a) => a.outcome === null) ? Math.min(wait, IN_FLIGHT_RECHECK_MS) : wait;
  }

  private waitFor(outcome: AttemptOutcome, limit: number, now: number): number {
    const held = this.attempts.filter((a) => a.outcome === outcome || a.outcome === null);
    return held.length < limit ? 0 : Math.min(...held.map((a) => a.at)) + this.windowMs - now;
  }

  /** The ceiling, and past `max` in the window the pace. A hard stop alone would leave a long outage up to 40 s to sit out. */
  private waitForFailed(now: number): number {
    const ceiling = this.waitFor('failed', this.failedMax, now);
    const held = this.attempts.filter((a) => a.outcome !== 'opened');
    if (held.length < this.max) return ceiling;
    return Math.max(ceiling, Math.max(...held.map((a) => a.at)) + this.windowMs / this.max - now);
  }

  take(now = Date.now()): ConnectAttempt {
    const attempt: ConnectAttempt = { at: now, outcome: null };
    this.attempts.push(attempt);
    return attempt;
  }

  /** The attempt's result. It counts from now: an open is what the server saw last. */
  settle(attempt: ConnectAttempt, outcome: AttemptOutcome, now = Date.now()): void {
    attempt.outcome = outcome;
    attempt.at = now;
  }
}

export interface WsManagerOptions {
  /** Defaults to `new WebSocket(url)` (global). */
  createSocket?: WebSocketFactory;
  pingIntervalMs?: number;
  pongTimeoutMs?: number;
  /** Abort a connect attempt that has not opened after this long. */
  connectTimeoutMs?: number;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
  /** A socket must stay up this long to clear the backoff. */
  stableResetMs?: number;
  /** Caps new sockets. Default none: no cap. */
  connectBudget?: ConnectBudget;
  /** Close the socket after this long with no subscriptions. Default none: it stays open. */
  idleCloseMs?: number;
  /** Default none. */
  counters?: WsCounters;
  random?: () => number;
  /** Called for `error` channel frames and listener exceptions. */
  onError?: (err: unknown) => void;
}

/** Extra delivery info: `replay` is true when the frame is a cached copy replayed to a late listener. */
export interface FrameMeta {
  replay: boolean;
}

export type Listener<S extends Subscription = Subscription> = (
  data: SubscriptionData<S>,
  sub: S,
  meta: FrameMeta,
) => void;

/** Streams whose last frame goes to a late listener (see header). */
const REPLAYED: ReadonlySet<SubscriptionType> = new Set<SubscriptionType>(['l2Book', 'bbo', 'allMids']);

interface SubEntry {
  sub: Subscription;
  key: string;
  listeners: Set<Listener>;
  /** The last frame of a REPLAYED stream. */
  latest: unknown;
}

const LIVE: FrameMeta = { replay: false };
const REPLAY: FrameMeta = { replay: true };

const OPEN = 1;

/** Channel name the server uses for each subscription type. */
const CHANNEL_OF: Record<SubscriptionType, string> = {
  l2Book: 'l2Book',
  trades: 'trades',
  allMids: 'allMids',
  bbo: 'bbo',
  activeAssetCtx: 'activeAssetCtx',
  spotState: 'spotState',
  clearinghouseState: 'clearinghouseState',
  openOrders: 'openOrders',
  orderUpdates: 'orderUpdates',
  userFills: 'userFills',
  userHistoricalOrders: 'userHistoricalOrders',
  userEvents: 'user',
  outcomeMetaUpdates: 'outcomeMetaUpdates',
};

function lower(v: unknown): string | undefined {
  return typeof v === 'string' ? v.toLowerCase() : undefined;
}

/** Identity fields carried by a data frame, used to pick the subscriptions it belongs to. */
interface FrameIdentity {
  coin?: string;
  user?: string;
  dex?: string;
}

function frameIdentity(channel: string, data: unknown): FrameIdentity {
  if (Array.isArray(data)) {
    // trades: [{coin,…}]; orderUpdates: [{order:{coin}}] carries no user.
    const first = data[0] as Record<string, unknown> | undefined;
    if (channel === 'trades' && first && typeof first.coin === 'string') return { coin: first.coin };
    return {};
  }
  if (data === null || typeof data !== 'object') return {};
  const d = data as Record<string, unknown>;
  return {
    coin: typeof d.coin === 'string' ? d.coin : undefined,
    user: lower(d.user),
    dex: typeof d.dex === 'string' ? d.dex : undefined,
  };
}

/** True when a data frame on `channel` belongs to subscription `sub`. */
export function frameMatches(sub: Subscription, channel: string, data: unknown): boolean {
  if (CHANNEL_OF[sub.type] !== channel) return false;
  const id = frameIdentity(channel, data);
  const s = sub as Record<string, unknown>;
  if (typeof s.coin === 'string' && id.coin !== undefined && id.coin !== s.coin) return false;
  if (typeof s.user === 'string' && id.user !== undefined && id.user !== s.user.toLowerCase()) return false;
  if (typeof s.dex === 'string' && id.dex !== undefined && id.dex !== s.dex) return false;
  if (sub.type === 'allMids' || sub.type === 'outcomeMetaUpdates') return true;
  // Market streams must carry the coin they are for; an empty trades array matches nothing.
  if (typeof s.coin === 'string' && id.coin === undefined) return false;
  return true;
}

export class WsManager {
  readonly url: string;
  private readonly opts: Required<Omit<WsManagerOptions, 'onError' | 'createSocket' | 'connectBudget' | 'idleCloseMs' | 'counters'>> & {
    onError?: (err: unknown) => void;
    connectBudget?: ConnectBudget;
    idleCloseMs?: number;
    counters?: WsCounters;
    createSocket: WebSocketFactory;
  };
  private socket: WebSocketLike | null = null;
  private subs = new Map<string, SubEntry>();
  private stateValue: WsState = 'closed';
  private stateListeners = new Set<(s: WsState) => void>();
  private reconnectHooks = new Set<() => void>();
  private attempt = 0;
  private everOpened = false;
  /** Date.now() when the current socket opened, 0 when it has not. */
  private openedAt = 0;
  /** The budget's record of the attempt in flight. */
  private inFlight: ConnectAttempt | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private lastMessageAt = 0;
  private wantOpen = false;
  private suspendedNow = false;
  /** The next open is the socket of a resume(). */
  private reopening = false;
  private resumedAtMs = 0;
  private onOnline = () => {
    if (this.wantOpen && this.stateValue === 'reconnecting') this.reconnectNow();
  };

  constructor(url: string, options: WsManagerOptions = {}) {
    this.url = url;
    this.opts = {
      createSocket: options.createSocket ?? ((u) => new WebSocket(u) as unknown as WebSocketLike),
      pingIntervalMs: options.pingIntervalMs ?? 30_000,
      pongTimeoutMs: options.pongTimeoutMs ?? 10_000,
      connectTimeoutMs: options.connectTimeoutMs ?? 10_000,
      backoffBaseMs: options.backoffBaseMs ?? 1_000,
      // Reference sites (S1) back off ~1/2/4/8 s capped near 10 s; equal jitter gives 0.5-1, 1-2, 2-4, 4-8, 5-10 s.
      backoffMaxMs: options.backoffMaxMs ?? 10_000,
      stableResetMs: options.stableResetMs ?? STABLE_RESET_MS,
      connectBudget: options.connectBudget,
      idleCloseMs: options.idleCloseMs,
      counters: options.counters,
      random: options.random ?? Math.random,
      onError: options.onError,
    };
  }

  // ------------------------------------------------------------------ public API

  get state(): WsState {
    return this.stateValue;
  }

  /** Number of distinct HL subscriptions currently held. */
  get subscriptionCount(): number {
    return this.subs.size;
  }

  /** Date.now() of the last frame of any kind (data, pong, ack) on the current socket, or of its open (#771). */
  get lastFrameAt(): number {
    return this.lastMessageAt;
  }

  /** Between suspend() and resume(). */
  get suspended(): boolean {
    return this.suspendedNow;
  }

  /** Date.now() of the last resume(), 0 if none. */
  get resumedAt(): number {
    return this.resumedAtMs;
  }

  onStateChange(cb: (s: WsState) => void): () => void {
    this.stateListeners.add(cb);
    return () => this.stateListeners.delete(cb);
  }

  /** Fires after a reconnect has re-sent all subscriptions (not on the first connect). */
  onReconnect(cb: () => void): () => void {
    this.reconnectHooks.add(cb);
    return () => this.reconnectHooks.delete(cb);
  }

  subscribe<S extends Subscription>(sub: S, listener: Listener<S>): () => void {
    this.stopIdleTimer();
    const key = canonicalJson(sub);
    let entry = this.subs.get(key);
    if (!entry) {
      entry = { sub, key, listeners: new Set(), latest: undefined };
      this.subs.set(key, entry);
      if (this.isOpen()) this.send({ method: 'subscribe', subscription: sub });
    }
    const l = listener as unknown as Listener;
    entry.listeners.add(l);
    this.connect();
    const owned = entry;
    let done = false;
    if (owned.latest !== undefined) {
      // Read now: a frame arriving before the microtask runs is delivered live.
      const frame = owned.latest;
      queueMicrotask(() => {
        if (done || !owned.listeners.has(l) || this.subs.get(key) !== owned) return;
        try {
          l(frame as never, owned.sub, REPLAY);
        } catch (e) {
          this.opts.onError?.(e);
        }
      });
    }
    return () => {
      if (done) return;
      done = true;
      owned.listeners.delete(l);
      if (owned.listeners.size > 0 || this.subs.get(key) !== owned) return;
      this.subs.delete(key);
      if (this.isOpen()) this.send({ method: 'unsubscribe', subscription: owned.sub });
      if (this.subs.size === 0) this.startIdleTimer();
    };
  }

  /** Opens the socket if it is not open/connecting. Idempotent. */
  connect(): void {
    this.wantOpen = true;
    if (this.suspendedNow || this.socket || this.reconnectTimer) return;
    if (typeof window !== 'undefined') window.addEventListener('online', this.onOnline);
    this.openSocket();
  }

  /** Tears down the socket, timers, subscriptions and hooks. The instance can be reused afterwards. */
  close(): void {
    this.subs.clear();
    this.reconnectHooks.clear();
    this.suspendedNow = false;
    this.release();
  }

  /** Closes the socket and stops every timer of it, keeping subscriptions and hooks. Listeners see `closed`. */
  suspend(): void {
    if (this.suspendedNow) return;
    this.suspendedNow = true;
    if (this.socket?.readyState === OPEN && this.opts.counters) this.opts.counters.closes++;
    this.settleAttempt('failed');
    this.clearTimers();
    this.stopIdleTimer();
    this.discardSocket();
    this.openedAt = 0;
    this.setState('closed');
  }

  /** Opens again at once, unless nothing wants this socket any more. */
  resume(): void {
    if (!this.suspendedNow) return;
    this.suspendedNow = false;
    this.resumedAtMs = Date.now();
    this.attempt = 0;
    if (!this.wantOpen) return;
    if (this.subs.size === 0) {
      this.release();
      return;
    }
    this.reopening = true;
    this.connect();
  }

  // ------------------------------------------------------------------ internals

  /** Drops the socket and every timer, back to before the first connect. Subscriptions and hooks are the caller's. */
  private release(): void {
    this.wantOpen = false;
    this.settleAttempt('failed');
    this.clearTimers();
    this.stopIdleTimer();
    if (typeof window !== 'undefined') window.removeEventListener('online', this.onOnline);
    this.discardSocket();
    this.attempt = 0;
    this.openedAt = 0;
    this.everOpened = false;
    this.reopening = false;
    this.setState('closed');
  }

  private discardSocket(): void {
    const s = this.socket;
    this.socket = null;
    if (!s) return;
    this.detach(s);
    try {
      s.close(1000, 'client close');
    } catch {
      /* ignore */
    }
  }

  private startIdleTimer(): void {
    const { idleCloseMs } = this.opts;
    if (idleCloseMs === undefined) return;
    this.stopIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      this.release();
    }, idleCloseMs);
  }

  private stopIdleTimer(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  private isOpen(): boolean {
    return this.stateValue === 'open' && this.socket !== null && this.socket.readyState === OPEN;
  }

  private setState(s: WsState): void {
    if (s === this.stateValue) return;
    this.stateValue = s;
    for (const cb of [...this.stateListeners]) {
      try {
        cb(s);
      } catch (e) {
        this.opts.onError?.(e);
      }
    }
  }

  private send(msg: unknown): void {
    try {
      this.socket?.send(JSON.stringify(msg));
    } catch (e) {
      this.opts.onError?.(e);
    }
  }

  private openSocket(): void {
    this.setState(this.everOpened ? 'reconnecting' : 'connecting');
    const budget = this.opts.connectBudget;
    const wait = budget?.waitMs() ?? 0;
    if (wait > 0) {
      this.retryIn(wait);
      return;
    }
    this.inFlight = budget?.take() ?? null;
    let s: WebSocketLike;
    try {
      s = this.opts.createSocket(this.url);
    } catch (e) {
      this.opts.onError?.(e);
      this.settleAttempt('failed');
      this.scheduleReconnect();
      return;
    }
    this.socket = s;
    this.connectTimer = setTimeout(() => {
      if (this.socket === s && s.readyState !== OPEN) this.dropSocket(s);
    }, this.opts.connectTimeoutMs);
    s.onopen = () => this.handleOpen(s);
    s.onmessage = (ev) => this.handleMessage(s, ev.data);
    s.onerror = () => {
      /* onclose follows; nothing to do */
    };
    s.onclose = () => {
      if (this.socket === s) this.dropSocket(s);
    };
  }

  private handleOpen(s: WebSocketLike): void {
    if (this.socket !== s) return;
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = null;
    this.settleAttempt('opened');
    if (this.reopening && this.opts.counters) this.opts.counters.reopens++;
    this.reopening = false;
    const isReconnect = this.everOpened;
    this.everOpened = true;
    this.openedAt = this.lastMessageAt = Date.now();
    this.setState('open');
    for (const e of this.subs.values()) this.send({ method: 'subscribe', subscription: e.sub });
    this.pingTimer = setInterval(() => this.ping(), this.opts.pingIntervalMs);
    if (isReconnect) {
      for (const cb of [...this.reconnectHooks]) {
        try {
          cb();
        } catch (e) {
          this.opts.onError?.(e);
        }
      }
    }
  }

  private settleAttempt(outcome: 'opened' | 'failed'): void {
    if (this.inFlight) this.opts.connectBudget?.settle(this.inFlight, outcome);
    this.inFlight = null;
  }

  private ping(): void {
    if (!this.isOpen()) return;
    this.send({ method: 'ping' });
    if (this.pongTimer) return;
    const sentAt = Date.now();
    const s = this.socket;
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      // Any frame after the ping proves the socket is alive (pong or data).
      if (this.socket !== s || !s || this.lastMessageAt >= sentAt) return;
      if (this.opts.counters) this.opts.counters.missedPongs++;
      this.dropSocket(s);
    }, this.opts.pongTimeoutMs);
  }

  private handleMessage(s: WebSocketLike, raw: unknown): void {
    if (this.socket !== s) return;
    this.lastMessageAt = Date.now();
    if (typeof raw !== 'string') return;
    let frame: WsFrame;
    try {
      frame = JSON.parse(raw) as WsFrame;
    } catch {
      return;
    }
    const { channel, data } = frame;
    if (channel === 'pong' || channel === 'subscriptionResponse') return;
    if (channel === 'error') {
      // "Already subscribed: …" / "Already unsubscribed: …" are benign races (e.g. a subscribe sent just as
      // the socket reconnected). Ref-counting means we never cause them on purpose; never surface them.
      if (typeof data === 'string' && /^Already (un)?subscribed/i.test(data)) return;
      this.opts.onError?.(new Error(`HL WS error: ${String(data)}`));
      return;
    }
    for (const entry of [...this.subs.values()]) {
      if (!frameMatches(entry.sub, channel, data)) continue;
      if (REPLAYED.has(entry.sub.type)) entry.latest = data;
      for (const l of [...entry.listeners]) {
        try {
          l(data as never, entry.sub, LIVE);
        } catch (e) {
          this.opts.onError?.(e);
        }
      }
    }
  }

  /** Socket died or was rejected: discard it and schedule a reconnect if still wanted. */
  private dropSocket(s: WebSocketLike): void {
    if (this.socket !== s) return;
    this.socket = null;
    this.settleAttempt('failed');
    if (this.openedAt > 0 && Date.now() - this.openedAt >= this.opts.stableResetMs) this.attempt = 0;
    this.openedAt = 0;
    this.clearTimers();
    this.detach(s);
    try {
      s.close();
    } catch {
      /* ignore */
    }
    if (this.wantOpen) this.scheduleReconnect();
    else this.setState('closed');
  }

  private scheduleReconnect(): void {
    this.setState('reconnecting');
    const exp = Math.min(this.opts.backoffMaxMs, this.opts.backoffBaseMs * 2 ** this.attempt);
    // "Equal jitter": half fixed, half random, so clients do not stampede after an outage.
    const delay = exp / 2 + this.opts.random() * (exp / 2);
    this.attempt++;
    this.retryIn(delay);
  }

  private retryIn(ms: number): void {
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wantOpen && !this.socket) this.openSocket();
    }, ms);
  }

  private reconnectNow(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (!this.socket) this.openSocket();
  }

  private detach(s: WebSocketLike): void {
    s.onopen = null;
    s.onclose = null;
    s.onerror = null;
    s.onmessage = null;
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.pongTimer) clearTimeout(this.pongTimer);
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.reconnectTimer = this.pingTimer = this.pongTimer = this.connectTimer = null;
  }
}
